import React from "react";

import { constructFiltersQuery } from "../utils/pagination.helper";

import { sortingAndSearchFilterParams } from "@/app/constants/constants";

interface UseSearchPaginationQueryConfig {
  /**
   * Map of source keys to rename to target keys
   * @example { 'projec_entity_search': 'search', 'sort_key': 'sortBy' }
   */
  keyMapping?: Record<string, string>;
}

interface UseSearchPaginationQueryOptions {
  /**
   * Custom filter parameters to include in the query
   * Defaults to sortingAndSearchFilterParams
   */
  filterParams?: string[];
  /**
   * Default pagination values
   */
  defaultPagination?: {
    limit?: number;
    currentPage?: number;
    offset?: number;
  };
  config?: UseSearchPaginationQueryConfig;
}

interface UseSearchPaginationQueryReturn {
  /**
   * The constructed query string for API calls
   */
  queryFilter: string;
  /**
   * Whether the current query has an active search term
   */
  hasActiveFilter: boolean;
  /**
   * The current search parameters as URLSearchParams object
   */
  searchParams: URLSearchParams;
  /**
   * Raw filter string from URL
   */
  filterString: string;
}

/**
 * Custom hook that intelligently combines search filters and pagination parameters
 *
 * Logic:
 * - When there's an active search: Only includes filter parameters and resets pagination to page 1
 * - When there's no search: Combines both filter and pagination parameters
 *
 * This prevents search queries from being limited to the current page.
 * @example
 * ```typescript
 * // Basic usage
 * const filter = useGetParams([...sortingAndSearchFilterParams]);
 * const { paginationQuery } = useLocalPaginationHelper();
 * const { queryFilter } = useSearchPaginationQuery(filter, paginationQuery);
 *
 * // With custom options
 * const { queryFilter, hasActiveSearch } = useSearchPaginationQuery(
 *   filter,
 *   paginationQuery,
 *   {
 *     filterParams: ['search', 'sortBy', 'orderBy', 'status'],
 *     defaultPagination: { limit: 50, currentPage: 1, offset: 0 }
 *   }
 * );
 *
 * // Use in API query
 * const { data } = useMyApiQuery({ filter: queryFilter });
 * ```
 * @param {string[]} filterString - The filter string from useGetParams
 * @param {string} paginationQuery - The pagination query string from useLocalPaginationHelper
 * @param {UseSearchPaginationQueryOptions} options - Configuration options
 * @returns {UseSearchPaginationQueryReturn} An object containing the constructed query filter, search parameters, and active search status
 */
export const useSearchPaginationQuery = (
  filterString: string,
  paginationQuery: string,
  options: UseSearchPaginationQueryOptions = {},
): UseSearchPaginationQueryReturn => {
  const {
    filterParams = sortingAndSearchFilterParams,
    defaultPagination = { limit: 25, currentPage: 1, offset: 0 },
    config,
  } = options;

  const keyMappingRef = React.useRef<Record<string, string>>({});
  const configKeyMapping = config?.keyMapping;

  const keyMapping = React.useMemo(() => {
    const newMapping = configKeyMapping || {};
    const prevMapping = keyMappingRef.current;

    const isSame =
      Object.keys(newMapping).length === Object.keys(prevMapping).length &&
      Object.entries(newMapping).every(
        ([key, value]) => prevMapping[key] === value,
      );

    if (!isSame) {
      keyMappingRef.current = newMapping;
    }

    return keyMappingRef.current;
  }, [configKeyMapping]);

  const reverseMapping = React.useMemo(
    () =>
      Object.entries(keyMapping).reduce<Record<string, string>>(
        (acc, [source, target]) => {
          acc[target] = source;

          return acc;
        },
        {},
      ),
    [keyMapping],
  );

  const paginationFilterRef = React.useRef<string>("");

  const safePaginationQuery = paginationQuery || "";

  const paginationParams = React.useMemo(
    () => new URLSearchParams(safePaginationQuery.replace(/^[?&]/, "")),
    [safePaginationQuery],
  );

  const hasOrderByInPagination = paginationParams.has("orderBy");

  const { params, hasActiveFilter } = React.useMemo(() => {
    if (!filterString && !hasOrderByInPagination) {
      return {
        params: new URLSearchParams(),
        hasActiveFilter: false,
      };
    }

    const normalized =
      filterString?.startsWith("?") || filterString?.startsWith("&")
        ? filterString.slice(1)
        : filterString || "";

    const params = new URLSearchParams(normalized);

    let hasMeaningfulFilter = false;
    params.forEach((value) => {
      if (value.trim() !== "") {
        hasMeaningfulFilter = true;
      }
    });

    return {
      params,
      hasActiveFilter: hasMeaningfulFilter || hasOrderByInPagination,
    };
  }, [filterString, hasOrderByInPagination]);

  const currentFilterString = params.toString();

  const prevPaginationRef = React.useRef<string>(safePaginationQuery);
  if (prevPaginationRef.current !== safePaginationQuery) {
    prevPaginationRef.current = safePaginationQuery;
    paginationFilterRef.current = currentFilterString;
  }

  const hasFilterChanged = currentFilterString !== paginationFilterRef.current;

  const queryFilter = React.useMemo(() => {
    if (hasActiveFilter) {
      const newRefinedQuery = new URLSearchParams();

      const mergedFilterParams = Array.from(
        new Set([...filterParams, "sortBy", "orderBy"]),
      );

      mergedFilterParams.forEach((param) => {
        const sourceKey = reverseMapping[param] || param;

        if (keyMapping[param]) {
          return;
        }

        const valuesFromFilter = params.getAll(sourceKey);

        if (valuesFromFilter.length > 0) {
          newRefinedQuery.set(param, valuesFromFilter.join(","));
        } else {
          const fallbackValue = paginationParams.get(sourceKey);
          if (fallbackValue && fallbackValue.trim() !== "") {
            newRefinedQuery.set(param, fallbackValue);
          }
        }
      });

      if (hasFilterChanged) {
        newRefinedQuery.set(
          "limit",
          paginationParams.get("limit") ||
            defaultPagination.limit?.toString() ||
            "25",
        );
        newRefinedQuery.set("currentPage", "1");
        newRefinedQuery.set("offset", "0");
      } else {
        newRefinedQuery.set(
          "limit",
          paginationParams.get("limit") ||
            defaultPagination.limit?.toString() ||
            "25",
        );
        newRefinedQuery.set(
          "currentPage",
          paginationParams.get("currentPage") ||
            defaultPagination.currentPage?.toString() ||
            "1",
        );
        newRefinedQuery.set(
          "offset",
          paginationParams.get("offset") ||
            defaultPagination.offset?.toString() ||
            "0",
        );
      }

      return "?" + newRefinedQuery.toString();
    } else {
      return constructFiltersQuery(filterString || "", safePaginationQuery);
    }
  }, [
    hasActiveFilter,
    params,
    filterParams,
    filterString,
    safePaginationQuery,
    paginationParams,
    defaultPagination,
    hasFilterChanged,
    reverseMapping,
    keyMapping,
  ]);

  return {
    queryFilter,
    hasActiveFilter,
    searchParams: params,
    filterString,
  };
};
